import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type TwoFactorMethod = "email" | "phone" | "whatsapp";

export interface EffectiveTwoFactor {
  enabled: boolean;
  method: TwoFactorMethod;
  /** where the decision came from: user | admin | role | global | default */
  source: "user" | "admin" | "role" | "global" | "default";
  /** true when the user cannot change it (imposed by the platform) */
  locked: boolean;
}

const DEFAULT_STATE: EffectiveTwoFactor = {
  enabled: false,
  method: "email",
  source: "default",
  locked: false,
};

export const TWO_FA_SESSION_KEY = "bera:2fa_verified";

export function markTwoFactorVerified(userId: string) {
  try {
    sessionStorage.setItem(TWO_FA_SESSION_KEY, userId);
  } catch {}
}

export function isTwoFactorVerified(userId: string) {
  try {
    return sessionStorage.getItem(TWO_FA_SESSION_KEY) === userId;
  } catch {
    return false;
  }
}

export function clearTwoFactorVerification() {
  try {
    sessionStorage.removeItem(TWO_FA_SESSION_KEY);
  } catch {}
}

/** Effective 2FA state for the signed-in user (individual > role > global > off). */
export function useTwoFactor() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["two-factor", user?.id],
    queryFn: async (): Promise<EffectiveTwoFactor> => {
      if (!user?.id) return DEFAULT_STATE;
      const { data, error } = await supabase.rpc("get_effective_2fa", { _user_id: user.id });
      if (error || !data) return DEFAULT_STATE;
      return { ...DEFAULT_STATE, ...(data as unknown as EffectiveTwoFactor) };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  const setOwnPreference = async (enabled: boolean, method: TwoFactorMethod = "email") => {
    if (!user?.id) return;
    const { error } = await supabase
      .from("user_2fa_settings")
      .upsert({ user_id: user.id, enabled, method }, { onConflict: "user_id" });
    if (error) throw error;
    await queryClient.invalidateQueries({ queryKey: ["two-factor", user.id] });
  };

  return {
    state: query.data ?? DEFAULT_STATE,
    loading: query.isLoading,
    refetch: query.refetch,
    setOwnPreference,
  };
}

/** Requests a fresh verification code for the current session. */
export async function requestTwoFactorCode() {
  const { data, error } = await supabase.functions.invoke("auth-2fa-request", { body: {} });
  if (error) throw error;
  return data as { required: boolean; method?: TwoFactorMethod; simulated?: boolean; dev_code?: string };
}

/** Verifies a code for the current session. */
export async function verifyTwoFactorCode(code: string) {
  const { data, error } = await supabase.functions.invoke("auth-2fa-verify", { body: { code } });
  if (error) return { verified: false, error: "invalid_code" as const };
  return data as { verified: boolean; error?: string; attemptsLeft?: number };
}
